// src/engine/AIEngine.ts
// UPGRADED VERSION with Transposition Table and Enhanced Move Ordering
// WITH DEBUG LOGGING

import { Board, Piece, Position, PieceColor } from "../types/Board.ts";
import { PositionEvaluator, EvaluationWeights } from "./PositionEvaluator.ts";
import { MoveGenerator, MovePriorityType, PrioritizedMove } from "./MoveGenerator.ts";
import { cloneBoard, setPieceAt, getPieceAt, getAllPieces } from "../utils/boardUtils.ts";
import { MoveValidator } from "../utils/moveValidator.ts";
import { ZobristHash } from "./ZobristHash.ts";
import { TranspositionTable, NodeType } from "./TranspositionTable.ts";
import { MoveOrdering } from "./MoveOrdering.ts";
import { PIECE_DEFINITIONS } from "../data/pieceDefinitions.ts";
import { PIECE_VALUES } from "../data/pieceValues.ts";

// Support both old and new difficulty level names
export type DifficultyLevel =
  | 'easy' | 'medium' | 'hard' | 'expert'  // Legacy names
  | 'beginner' | 'casual' | 'intermediate' | 'advanced' | 'master';  // New v1.30+ names

export interface AISettings {
  difficulty: DifficultyLevel;
  weights: EvaluationWeights;
  priorityOrder: MovePriorityType[];
  maxThinkingTime: number; // seconds
  useIterativeDeepening: boolean;
  useAlphaBetaPruning: boolean;
  useQuiescenceSearch?: boolean;
  maxQuiescenceDepth?: number;
  useTranspositionTable?: boolean;
  ttSizeMB?: number;
}

export interface AIMove {
  from: Position;
  to: Position;
  piece: Piece;
  capturedPiece?: Piece;
  score: number;
  thinkingTime: number;
  nodesEvaluated: number;
  depth: number;
  ttHitRate?: number;
  ttCutoffRate?: number;
}

export class AIEngine {
  private evaluator: PositionEvaluator;
  private moveGenerator: MoveGenerator;
  private moveValidator: MoveValidator;
  private settings: AISettings;
  private nodesEvaluated: number = 0;
  private yieldCounter: number = 0;
  
  private zobristHash: ZobristHash;
  private transpositionTable: TranspositionTable;
  private moveOrdering: MoveOrdering;
  private currentPositionHash: bigint = 0n;

  constructor(settings: AISettings) {
    this.settings = {
      ...settings,
      useQuiescenceSearch: settings.useQuiescenceSearch ?? true,
      maxQuiescenceDepth: settings.maxQuiescenceDepth ?? 10,
      useTranspositionTable: settings.useTranspositionTable ?? true,
      ttSizeMB: settings.ttSizeMB ?? 64,
    };
    
    this.evaluator = new PositionEvaluator(settings.weights);
    this.moveGenerator = new MoveGenerator(this.evaluator, settings.priorityOrder);
    this.moveValidator = new MoveValidator();
    
    this.zobristHash = new ZobristHash(64, 8);
    this.transpositionTable = new TranspositionTable(this.settings.ttSizeMB);
    this.moveOrdering = new MoveOrdering(64);
    
    console.log('[AI] Engine initialized with Transposition Table');
  }

  private initializeForBoard(board: Board): void {
    const boardSize = board.dimensions.rows * board.dimensions.cols;
    const numFiles = board.dimensions.cols;
    
    this.zobristHash = new ZobristHash(boardSize, numFiles);
    this.moveOrdering = new MoveOrdering(boardSize);
  }

  updateSettings(settings: AISettings): void {
    this.settings = {
      ...settings,
      useQuiescenceSearch: settings.useQuiescenceSearch ?? true,
      maxQuiescenceDepth: settings.maxQuiescenceDepth ?? 10,
      useTranspositionTable: settings.useTranspositionTable ?? true,
      ttSizeMB: settings.ttSizeMB ?? 64,
    };
    
    this.evaluator.setWeights(settings.weights);
    this.moveGenerator.setPriorityOrder(settings.priorityOrder);
    
    if (settings.ttSizeMB && settings.ttSizeMB !== this.transpositionTable['size']) {
      this.transpositionTable = new TranspositionTable(settings.ttSizeMB);
    }
  }

  private async maybeYield(): Promise<void> {
    this.yieldCounter++;
    if (this.yieldCounter % 100 === 0) {
      await new Promise(resolve => setTimeout(resolve, 0));
    }
  }

  /**
   * Get the best move for the AI
   */
  async getBestMove(board: Board, color: PieceColor): Promise<AIMove | null> {
    const startTime = Date.now();
    this.nodesEvaluated = 0;
    this.yieldCounter = 0;
    
    console.log('[AI] getBestMove called for color:', color);
    
    // DEBUG: Check board state
    const allPieces = getAllPieces(board);
    const colorPieces = getAllPieces(board, color);
    console.log(`[AI] Board has ${allPieces.length} total pieces, ${colorPieces.length} ${color} pieces`);
    
    if (colorPieces.length === 0) {
      console.error('[AI] No pieces found for color:', color);
      return null;
    }
    
    // DEBUG: Check if pieces have rules
    const piecesWithRules = colorPieces.filter(p => p.rules && p.rules.movements && p.rules.movements.length > 0);
    console.log(`[AI] ${color} pieces with valid movement rules: ${piecesWithRules.length}/${colorPieces.length}`);
    
    if (piecesWithRules.length === 0) {
      console.error('[AI] No pieces have valid movement rules!');
      console.log('[AI] Sample piece:', colorPieces[0]);
      return null;
    }
    
    // Initialize TT for this board size
    this.initializeForBoard(board);
    
    // Start new search
    this.transpositionTable.newSearch();
    this.moveOrdering.clearKillers();
    
    // Compute initial position hash
    this.currentPositionHash = this.zobristHash.computeHash(board, color);
    
    const depth = this.getSearchDepth();
    console.log('[AI] Search depth:', depth);
    
    let bestMove: AIMove | null = null;
    let pvMove: PrioritizedMove | null = null;

    if (this.settings.useIterativeDeepening) {
      for (let currentDepth = 1; currentDepth <= depth; currentDepth++) {
        const elapsedTime = (Date.now() - startTime) / 1000;
        
        if (elapsedTime >= this.settings.maxThinkingTime) {
          console.log('[AI] Time limit reached at depth', currentDepth);
          break;
        }

        console.log(`[AI] Searching at depth ${currentDepth}...`);
        const move = await this.searchBestMove(board, color, currentDepth, startTime, pvMove);
        
        if (move) {
          bestMove = move;
          console.log(`[AI] Found move at depth ${currentDepth}:`, move.from, '->', move.to);
          pvMove = {
            from: move.from,
            to: move.to,
            piece: move.piece,
            capturedPiece: move.capturedPiece,
            priority: 0,
            priorityType: 'pv',
            estimatedValue: move.score
          };
        } else {
          console.log(`[AI] No move found at depth ${currentDepth}`);
        }

        if (bestMove && bestMove.score > 9000) {
          break;
        }
      }
    } else {
      bestMove = await this.searchBestMove(board, color, depth, startTime, pvMove);
    }

    if (bestMove) {
      bestMove.thinkingTime = (Date.now() - startTime) / 1000;
      bestMove.nodesEvaluated = this.nodesEvaluated;
      
      if (this.settings.useTranspositionTable) {
        bestMove.ttHitRate = this.transpositionTable.getHitRate();
        bestMove.ttCutoffRate = this.transpositionTable.getCutoffRate();
      }
      
      if (this.settings.useTranspositionTable) {
        this.transpositionTable.printStats();
      }
    } else {
      console.error('[AI] getBestMove returning null - no move found');
    }

    return bestMove;
  }

  private getSearchDepth(): number {
    // Map both legacy and new difficulty names to search depths
    const depths: Record<string, number> = {
      // Legacy names
      easy: 2,
      medium: 4,
      hard: 6,
      expert: 8,
      // New v1.30+ difficulty names
      beginner: 2,
      casual: 4,
      intermediate: 6,
      advanced: 8,
      master: 10,
    };
    const depth = depths[this.settings.difficulty];
    if (depth === undefined) {
      console.warn(`[AI] Unknown difficulty level: ${this.settings.difficulty}, defaulting to intermediate (6)`);
      return 6;
    }
    return depth;
  }

  private async searchBestMove(
    board: Board,
    color: PieceColor,
    depth: number,
    startTime: number,
    pvMove: PrioritizedMove | null = null
  ): Promise<AIMove | null> {
    console.log(`[AI] searchBestMove: generating moves for ${color} at depth ${depth}`);
    
    const moves = this.moveGenerator.generateOrderedMoves(
      board, 
      color, 
      this.moveOrdering.getKillers(depth), 
      pvMove || undefined
    );
    
    console.log(`[AI] generateOrderedMoves returned ${moves.length} moves`);
    
    if (moves.length === 0) {
      // DEBUG: Why no moves?
      const pieces = getAllPieces(board, color);
      console.error(`[AI] No moves generated! ${color} has ${pieces.length} pieces`);
      
      if (pieces.length > 0) {
        // Try to get legal moves for first piece manually
        const testPiece = pieces[0];
        console.log('[AI] Testing first piece:', testPiece.type, 'at', testPiece.position);
        try {
          const legalMoves = this.moveValidator.getLegalMoves(testPiece, board);
          console.log(`[AI] Legal moves for ${testPiece.type}: ${legalMoves.length}`);
        } catch (e) {
          console.error('[AI] Error getting legal moves:', e);
        }
      }
      
      return null;
    }
    
    // Log first few moves
    console.log('[AI] First 3 moves:', moves.slice(0, 3).map(m => ({
      piece: m.piece.type,
      from: m.from,
      to: m.to,
      priority: m.priority
    })));

    let bestMove: AIMove | null = null;
    let bestScore = -Infinity;
    let alpha = -Infinity;
    let beta = Infinity;

    for (const move of moves) {
      await this.maybeYield();

      const elapsedTime = (Date.now() - startTime) / 1000;
      if (elapsedTime >= this.settings.maxThinkingTime) {
        break;
      }

      const testBoard = cloneBoard(board);
      setPieceAt(testBoard, move.from, null);
      setPieceAt(testBoard, move.to, { ...move.piece, position: move.to, hasMoved: true });

      const newHash = this.zobristHash.updateHashForMove(
        this.currentPositionHash,
        move.piece,
        move.from,
        move.to,
        move.capturedPiece
      );
      const newHashWithSide = this.zobristHash.updateHashForSideChange(newHash);

      const score = this.settings.useAlphaBetaPruning
        ? await this.alphaBeta(
            testBoard, 
            depth - 1, 
            -beta, 
            -alpha, 
            color === 'white' ? 'black' : 'white', 
            startTime, 
            depth, 
            newHashWithSide,
            0,
            move
          )
        : await this.minimax(testBoard, depth - 1, color === 'white' ? 'black' : 'white', startTime);

      if (-score > bestScore) {
        bestScore = -score;
        bestMove = {
          from: move.from,
          to: move.to,
          piece: move.piece,
          capturedPiece: move.capturedPiece,
          score: bestScore,
          thinkingTime: 0,
          nodesEvaluated: 0,
          depth
        };
      }

      if (this.settings.useAlphaBetaPruning) {
        alpha = Math.max(alpha, -score);
        if (alpha >= beta) {
          if (!move.capturedPiece && this.settings.useTranspositionTable) {
            this.moveOrdering.addKiller(move, depth);
          }
          break;
        }
      }
    }

    return bestMove;
  }

  private async minimax(
    board: Board,
    depth: number,
    color: PieceColor,
    startTime: number
  ): Promise<number> {
    this.nodesEvaluated++;
    await this.maybeYield();

    const elapsedTime = (Date.now() - startTime) / 1000;
    if (elapsedTime >= this.settings.maxThinkingTime) {
      return this.evaluator.evaluatePosition(board, color).score;
    }

    if (depth === 0) {
      if (this.settings.useQuiescenceSearch) {
        return await this.quiescence(board, -Infinity, Infinity, color, startTime, 0);
      }
      return this.evaluator.evaluatePosition(board, color).score;
    }

    if (this.moveValidator.isCheckmate(board, color)) {
      return -10000 - depth;
    }
    if (this.moveValidator.isStalemate(board, color)) {
      return 0;
    }

    const moves = this.moveGenerator.generateOrderedMoves(board, color);
    if (moves.length === 0) {
      return this.evaluator.evaluatePosition(board, color).score;
    }

    let bestScore = -Infinity;

    for (const move of moves) {
      const testBoard = cloneBoard(board);
      setPieceAt(testBoard, move.from, null);
      setPieceAt(testBoard, move.to, { ...move.piece, position: move.to, hasMoved: true });

      const score = await this.minimax(testBoard, depth - 1, color === 'white' ? 'black' : 'white', startTime);
      bestScore = Math.max(bestScore, -score);
    }

    return bestScore;
  }

  private async alphaBeta(
    board: Board,
    depth: number,
    alpha: number,
    beta: number,
    color: PieceColor,
    startTime: number,
    rootDepth: number = 0,
    positionHash: bigint = 0n,
    ply: number = 0,
    previousMove: PrioritizedMove | null = null
  ): Promise<number> {
    this.nodesEvaluated++;
    await this.maybeYield();

    const elapsedTime = (Date.now() - startTime) / 1000;
    if (elapsedTime >= this.settings.maxThinkingTime) {
      return this.evaluator.evaluatePosition(board, color).score;
    }

    let ttMove: PrioritizedMove | undefined;
    if (this.settings.useTranspositionTable && depth > 0) {
      const ttResult = this.transpositionTable.probe(positionHash, depth, alpha, beta, ply);
      
      if (ttResult.cutoff) {
        return ttResult.score!;
      }
      
      ttMove = ttResult.bestMove;
    }

    if (depth === 0) {
      if (this.settings.useQuiescenceSearch) {
        return await this.quiescence(board, alpha, beta, color, startTime, 0);
      }
      return this.evaluator.evaluatePosition(board, color).score;
    }

    if (this.moveValidator.isCheckmate(board, color)) {
      return -10000 - depth;
    }
    if (this.moveValidator.isStalemate(board, color)) {
      return 0;
    }

    const currentDepth = rootDepth - depth;
    const killers = this.moveOrdering.getKillers(currentDepth);
    const moves = this.moveGenerator.generateOrderedMoves(board, color, killers, ttMove);
    
    if (moves.length === 0) {
      return this.evaluator.evaluatePosition(board, color).score;
    }

    if (this.settings.useTranspositionTable) {
      for (const move of moves) {
        if (!move.capturedPiece) {
          const historyScore = this.moveOrdering.getHistoryScore(move, color, board.dimensions.cols);
          const counterScore = this.moveOrdering.getCounterMoveScore(move, previousMove);
          move.priority += historyScore + counterScore;
        }
      }
      
      moves.sort((a, b) => b.priority - a.priority);
    }

    let value = -Infinity;
    let bestMove: PrioritizedMove | null = null;
    let nodeType = NodeType.UPPER_BOUND;
    const failedMoves: PrioritizedMove[] = [];

    for (const move of moves) {
      const testBoard = cloneBoard(board);
      setPieceAt(testBoard, move.from, null);
      setPieceAt(testBoard, move.to, { ...move.piece, position: move.to, hasMoved: true });

      const newHash = this.zobristHash.updateHashForMove(
        positionHash,
        move.piece,
        move.from,
        move.to,
        move.capturedPiece
      );
      const newHashWithSide = this.zobristHash.updateHashForSideChange(newHash);

      const score = await this.alphaBeta(
        testBoard,
        depth - 1,
        -beta,
        -alpha,
        color === 'white' ? 'black' : 'white',
        startTime,
        rootDepth,
        newHashWithSide,
        ply + 1,
        move
      );
      
      const negScore = -score;
      
      if (negScore > value) {
        value = negScore;
        bestMove = move;
        
        if (value > alpha) {
          alpha = value;
          nodeType = NodeType.EXACT;
          
          if (alpha >= beta) {
            nodeType = NodeType.LOWER_BOUND;
            
            if (this.settings.useTranspositionTable && !move.capturedPiece) {
              this.moveOrdering.addKiller(move, currentDepth);
              this.moveOrdering.addCounterMove(previousMove, move);
              this.moveOrdering.updateHistory(move, color, depth, board.dimensions.cols);
              
              for (const failed of failedMoves) {
                if (!failed.capturedPiece) {
                  this.moveOrdering.penalizeHistory(failed, color, depth, board.dimensions.cols);
                }
              }
            }
            
            break;
          }
        } else {
          failedMoves.push(move);
        }
      } else {
        failedMoves.push(move);
      }
    }

    if (this.settings.useTranspositionTable) {
      this.transpositionTable.store(positionHash, depth, value, nodeType, bestMove, ply);
    }

    return value;
  }

  private async quiescence(
    board: Board,
    alpha: number,
    beta: number,
    color: PieceColor,
    startTime: number,
    qDepth: number
  ): Promise<number> {
    this.nodesEvaluated++;
    await this.maybeYield();

    const elapsedTime = (Date.now() - startTime) / 1000;
    if (elapsedTime >= this.settings.maxThinkingTime) {
      return this.evaluator.evaluatePosition(board, color).score;
    }

    const maxQDepth = this.settings.maxQuiescenceDepth || 10;
    if (qDepth >= maxQDepth) {
      return this.evaluator.evaluatePosition(board, color).score;
    }

    const standPat = this.evaluator.evaluatePosition(board, color).score;

    if (standPat >= beta) {
      return beta;
    }

    if (alpha < standPat) {
      alpha = standPat;
    }

    const tacticalMoves = this.moveGenerator.generateTacticalMoves(board, color);

    if (tacticalMoves.length === 0) {
      return standPat;
    }

    const DELTA_MARGIN = 9.0;
    if (standPat < alpha - DELTA_MARGIN) {
      const hasHighValueCapture = tacticalMoves.some(m => 
        m.capturedPiece && this.getPieceValueForDelta(m.capturedPiece, board) > DELTA_MARGIN * 0.5
      );
      
      if (!hasHighValueCapture) {
        return alpha;
      }
    }

    for (const move of tacticalMoves) {
      const testBoard = cloneBoard(board);
      setPieceAt(testBoard, move.from, null);
      setPieceAt(testBoard, move.to, { ...move.piece, position: move.to, hasMoved: true });

      const score = -await this.quiescence(
        testBoard,
        -beta,
        -alpha,
        color === 'white' ? 'black' : 'white',
        startTime,
        qDepth + 1
      );

      if (score >= beta) {
        return beta;
      }

      if (score > alpha) {
        alpha = score;
      }
    }

    return alpha;
  }

  private getPieceValueForDelta(piece: Piece, board: Board): number {
    const boardSizeStr = `${board.dimensions.rows}Ã—${board.dimensions.rows}`;
    const definition = PIECE_DEFINITIONS[piece.type.toLowerCase()];
    if (!definition) return 0;

    const abbreviation = definition.abbreviation.toUpperCase();
    const pieceValues = PIECE_VALUES[abbreviation];
    
    if (!pieceValues) return 0;

    const valueEntry = pieceValues.find(v => v.boardSize === boardSizeStr);
    return valueEntry ? valueEntry.value : 0;
  }

  getStatistics(): { 
    nodesEvaluated: number;
    ttHitRate?: number;
    ttCutoffRate?: number;
  } {
    const stats: any = { nodesEvaluated: this.nodesEvaluated };
    
    if (this.settings.useTranspositionTable) {
      stats.ttHitRate = this.transpositionTable.getHitRate();
      stats.ttCutoffRate = this.transpositionTable.getCutoffRate();
    }
    
    return stats;
  }
}
